// src/hooks/useAuth.ts
// Auth state management hook

import { useEffect, useState } from 'react'
import { Session, User } from '@supabase/supabase-js'
import { supabase } from '../api/supabase'

export interface PlanSyncAccount {
  id: string
  phone_e164: string
  display_name: string | null
  role: 'customer' | 'vendor_member' | 'super_admin'
  status: 'active' | 'suspended' | 'pending_telegram_connection'
  phone_verified_at: string | null
  telegram_user_id: number | null
  telegram_username: string | null
  telegram_required: boolean
  telegram_connected_at: string | null
  preferred_notification_channel: string
  last_login_at: string | null
  created_at: string
}

export interface UseAuthReturn {
  loading: boolean
  user: User | null
  session: Session | null
  account: PlanSyncAccount | null
  isAuthenticated: boolean
  isCustomer: boolean
  isVendor: boolean
  isSuperAdmin: boolean
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

export function useAuth(): UseAuthReturn {
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [account, setAccount] = useState<PlanSyncAccount | null>(null)
  
  async function fetchAccount(userId: string) {
    const { data, error } = await supabase
      .from('accounts')
      .select('*')
      .eq('id', userId)
      .single()
    
    if (error) {
      console.error('Failed to fetch account:', error)
      setAccount(null)
      return
    }
    
    setAccount(data as PlanSyncAccount)
  }
  
  async function refresh() {
    if (user) {
      await fetchAccount(user.id)
    }
  }
  
  useEffect(() => {
    let mounted = true

    // Hard timeout: if session check + account fetch don't resolve
    // within 15s, force loading=false so AuthGate doesn't spin forever.
    //
    // 15s balances two real failure modes seen in production:
    //   • Hung promises (getSession never resolves) — needs an upper bound
    //   • Slow Maldives 4G / first-load fetch — legitimately takes 5–10s
    // 5s was too aggressive: it fired during normal post-OTP account
    // fetches and kicked the user back to /auth/login.
    const timeout = setTimeout(() => {
      if (mounted) setLoading(false)
    }, 15_000)

    // Get initial session
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (!mounted) return

        setSession(session)
        setUser(session?.user ?? null)

        if (session?.user) {
          fetchAccount(session.user.id).finally(() => {
            if (mounted) {
              clearTimeout(timeout)
              setLoading(false)
            }
          })
        } else {
          clearTimeout(timeout)
          setLoading(false)
        }
      })
      .catch(err => {
        // getSession() should never reject under normal conditions, but
        // network blips and Supabase SDK edge cases sometimes hang the
        // promise. Make sure loading always resolves.
        // eslint-disable-next-line no-console
        console.warn('useAuth: getSession failed', err)
        if (mounted) {
          clearTimeout(timeout)
          setLoading(false)
        }
      })

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, newSession) => {
        if (!mounted) return

        setSession(newSession)
        setUser(newSession?.user ?? null)

        if (newSession?.user) {
          await fetchAccount(newSession.user.id)
        } else {
          setAccount(null)
        }
      }
    )

    return () => {
      mounted = false
      clearTimeout(timeout)
      subscription.unsubscribe()
    }
  }, [])
  
  async function signOut() {
    await supabase.auth.signOut()
    setUser(null)
    setSession(null)
    setAccount(null)
  }
  
  return {
    loading,
    user,
    session,
    account,
    isAuthenticated: !!user && !!account,
    isCustomer: account?.role === 'customer',
    isVendor: account?.role === 'vendor_member',
    isSuperAdmin: account?.role === 'super_admin',
    signOut,
    refresh
  }
}
